<template>
  <v-form ref="form"
          method="post"
          enctype="multipart/form-data"
          @submit.prevent="onSubmit">
    <v-card-text>
      <v-autocomplete label="Prediction"
                      :items="predictions"
                      item-text="name"
                      item-value="uuid"
                      v-model="uuid"
                      required
                      clearable />
      <v-autocomplete label="Group (optional)"
                      :items="group_options"
                      item-text="name"
                      item-value="id"
                      v-model="config.group"
                      hint="Fills in this group's own wells, method, technique, and enabled targets below -- still freely editable afterward"
                      persistent-hint
                      clearable />
      <v-row>
        <v-col>
          <v-autocomplete label="Targets"
                          :items="target_options"
                          v-model="config.targets"
                          :rules="rules.minimum"
                          multiple
                          required
                          clearable />
        </v-col>
        <v-col>
          <v-autocomplete label="Method"
                          :items="method_options"
                          v-model="config.method"
                          :rules="rules.required"
                          required
                          clearable />
        </v-col>
        <v-col>
          <v-autocomplete v-model="config.technique"
                          :items="techniques"
                          item-text="name"
                          item-value="slug"
                          label="Technique"
                          clearable></v-autocomplete>
        </v-col>
        <v-col>
          <v-select label="Chosen ID"
                    :items="well_id_formats"
                    v-model="config.well_id_format"
                    :rules="rules.required"
                    required />
        </v-col>
      </v-row>
      <v-alert v-if="termination_display"
               dense
               text
               type="info"
               class="mb-4">
        Terminal decline already applied to this prediction at creation: {{ termination_display }}
      </v-alert>
      <v-row class="align-center">
        <v-col cols="auto">
          <v-checkbox label="Terminate forecasts"
                      v-model="config.terminate"></v-checkbox>
        </v-col>
        <v-col cols="auto">
          <v-radio-group v-model="config.strategy"
                         row
                         :disabled="!config.terminate">
            <v-radio v-for="(option, o) in strategies"
                     :key="o"
                     :label="option.text"
                     :value="option.value"
                     required
                     :disabled="!config.terminate" />
          </v-radio-group>
        </v-col>
        <v-col>
          <v-text-field v-model.number="config.limit"
                        :suffix="strategy_suffix"
                        class="mt-0 pt-0"
                        type="number"
                        :hint="strategy_hint"
                        hide-spin-buttons
                        persistent-hint
                        required
                        :disabled="!config.terminate" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-btn depressed
             class="success"
             type="submit"
             :disabled="!isValid">Generate</v-btn>
      <v-btn text
             type="button"
             @click="$router.go(-1)">Cancel</v-btn>
    </v-card-actions>
  </v-form>
</template>
<script>
import Predictions from "@/api/predictions"
export default {
  name: "ExportComboCurveForm",
  props: {
    predictions: { type: Array, required: true },
    techniques: { type: Array, required: true },
    instance: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      uuid: null,
      prediction: null,
      // Only 'duration' and 'production' -- a percent-based terminal
      // decline is only ever configured once, at prediction creation
      // (Forms/Prediction.vue's Termination section), and the backend
      // reads it from there automatically for every combocurve export
      // (see termination_display below). A third, redundant 'decline'
      // cutoff strategy isn't offered here the way it is on the default
      // export tab, since there's nothing left for it to configure.
      strategies: [
        { text: 'Duration', value: 'duration', hint: 'Cut off forecasts after a certain number of months', unit: 'months' },
        { text: 'Production', value: 'production', hint: 'Cut off forecasts when monthly volume falls below a certain threshold', unit: 'bbl/m' },
      ],
      // Which well identifier becomes ComboCurve's Chosen ID column.
      well_id_formats: ['API 10', 'API 14'],
      config: {
        group: null,
        // Not user-editable directly -- set only as a side effect of
        // picking a Group (below), carrying that group's well list
        // through to submission. null exports every well, same as always.
        wells: null,
        targets: [],
        method: null,
        technique: null,
        well_id_format: 'API 10',
        terminate: false,
        strategy: null,
        limit: null,
      },
      rules: {
        required: [v => !!v || "Field is required"],
        minimum: [v => v.length > 0 || "Minimum one is required"],
      },
    }
  },
  mounted() {
    if (!this.isEmpty(this.instance)) {
      this.uuid = this.instance.prediction.uuid
      let cfg = this.instance.config || {}
      this.config = {
        group: cfg.group != null ? cfg.group : null,
        wells: cfg.wells || null,
        targets: cfg.targets || [],
        method: (cfg.methods && cfg.methods[0]) || null,
        technique: cfg.technique || null,
        well_id_format: cfg.well_id_format || 'API 10',
        terminate: cfg.terminate || false,
        strategy: cfg.strategy || null,
        limit: cfg.limit || null,
      }
    }
  },
  watch: {
    uuid: function (val) {
      if (val) {
        Predictions.retrieve(val, `{id,uuid,name}`).then(response => {
          this.prediction = response.data
        })
      } else {
        this.config.group = null
        this.config.wells = null
        this.config.targets = []
        this.config.method = null
        this.config.technique = null
        this.config.well_id_format = 'API 10'
        this.config.terminate = false
        this.config.strategy = null
        this.config.limit = null
      }
    },
    "config.terminate": function (val) {
      if (!val) {
        this.config.strategy = null
        this.config.limit = null
      }
    },
    "config.group": function (val) {
      if (val) {
        // Seeds targets/method/technique as defaults from the group's own
        // current settings -- freely editable afterward, same as a
        // group-less export. Picking a different group re-seeds them
        // again; nothing here is re-applied at generation time (see
        // models/export.py's generate()), so an edit made after this point
        // always sticks. wells is deliberately NOT seeded from
        // group.wells here -- that snapshot has proven unreliable, so the
        // backend re-derives it fresh from the group's own filters at
        // generation time instead (same call sync_groups() makes), every
        // time, picking up any filter narrowed down since the group was
        // created.
        let group = this.group_options.find(g => g.id == val)
        if (group) {
          this.config.targets = Object.entries(group.targets || {})
            .filter(([, t]) => t.enabled)
            .map(([target]) => target)
          this.config.method = group.settings.method
          this.config.technique = group.settings.technique
        }
      } else {
        this.config.targets = []
        this.config.method = null
        this.config.technique = null
      }
    },
  },
  computed: {
    target_options() {
      if (!this.prediction) return []
      let targets = []
      for (let [key, value] of Object.entries(this.prediction.config.targets)) {
        if (value != null) targets.push({ text: this.capitalize(key), value: key })
      }
      // When a group is selected, only its own enabled targets are valid
      // choices -- still a free multi-select within that set, e.g. picking
      // fewer than all of them, just not one the group itself has disabled.
      if (this.config.group) {
        let group = this.group_options.find(g => g.id == this.config.group)
        if (group) {
          let enabled = Object.entries(group.targets || {})
            .filter(([, t]) => t.enabled)
            .map(([target]) => target)
          targets = targets.filter(t => enabled.includes(t.value))
        }
      }
      return targets
    },
    method_options() {
      if (!this.prediction) return []
      let methods = []
      this.prediction.methods.forEach(element => {
        methods.push({ text: element.name, value: element.slug })
      })
      return methods
    },
    // Every group on this prediction -- not just locked ones. A group's
    // wells/settings/enabled-targets are captured the moment it's created
    // (Forecasting.vue's onCreateGroup), so locking/syncing (which only
    // matters for a group's own edited params/curves/termination, never
    // read here) isn't a prerequisite for this.
    group_options() {
      if (!this.prediction) return []
      return this.prediction.groups || []
    },
    strategy_suffix() {
      if (!this.config.strategy) return ''
      let strategy = this.strategies.find(s => s.value == this.config.strategy)
      if (strategy && strategy.unit) return strategy.unit
      return ''
    },
    strategy_hint() {
      if (!this.config.strategy) return ''
      let strategy = this.strategies.find(s => s.value == this.config.strategy)
      if (strategy && strategy.hint) return strategy.hint
      return ''
    },
    // Read-only -- reflects whatever config.termination the prediction
    // itself was already created with (Forms/Prediction.vue's Termination
    // section); the backend sources this automatically for a combocurve
    // export, so there's nothing to re-enter here, only to display.
    termination_display() {
      if (!this.prediction) return null
      let termination = this.prediction.config.termination
      if (!termination || !termination.enabled) return null
      let parts = this.config.targets
        .filter(target => termination[target] != null)
        .map(target => `${this.capitalize(target)} ${termination[target]}%`)
      return parts.length > 0 ? parts.join(', ') : null
    },
    isValid() {
      let targets = !this.isEmpty(this.config.targets)
      let method = !!this.config.method
      let technique = !!this.config.technique
      let well_id_format = !!this.config.well_id_format
      let terminate = !!this.config.terminate
      let strategy = !!this.config.strategy
      let limit = !!this.config.limit

      if (!(targets && method && technique && well_id_format)) return false
      if (terminate && !strategy) return false
      if (terminate && strategy && !limit) return false

      return true
    },
  },
  methods: {
    onSubmit() {
      if (this.$refs.form.validate()) {
        let data = {
          prediction: this.prediction.id,
          config: {
            format: 'combocurve',
            // Bookkeeping only -- which group (if any) seeded the fields
            // below, so re-opening this export for editing shows it
            // selected again. Never read at generation time; wells/
            // targets/methods/technique are always used exactly as saved.
            group: this.config.group,
            wells: this.config.wells,
            targets: this.config.targets,
            // Backend caps a combocurve export to exactly one method, but
            // still expects "methods" as a list -- same param shape as the
            // default export's config, so Export.generate() can dispatch
            // on params["format"] without either path needing a different
            // key name for the same concept.
            methods: [this.config.method],
            technique: this.config.technique,
            well_id_format: this.config.well_id_format,
            terminate: this.config.terminate,
            strategy: this.config.strategy,
            limit: this.config.limit,
          }
        }
        this.$emit("submitted", data)
      }
    },
    isEmpty(obj) {
      return Object.keys(obj).length === 0
    },
    capitalize(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
  }
}
</script>
